import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { responsiblesService, Responsible, CreateResponsibleData } from '../../services/responsibles';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';

const roleLabels = {
  manager: 'Gerente',
  kitchen: 'Cocina',
};

const statusLabels = {
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
  pending: 'Pendiente',
};

const statusColors = {
  active: '#22b6b2',
  inactive: '#9CA3AF',
  suspended: '#EF4444',
  pending: '#F59E0B',
};

const registrationLabels = {
  pending: 'Pendiente de registro',
  registered: 'Registrado',
};

const registrationColors = {
  pending: '#FF9800',
  registered: '#4CAF50',
};

const ResponsibleCard = ({ responsible, onEdit, onToggleStatus, onDelete }: {
  responsible: Responsible;
  onEdit: (responsible: Responsible) => void;
  onToggleStatus: (responsible: Responsible) => void;
  onDelete: (responsible: Responsible) => void;
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="relative group">
      {/* Efecto de fondo */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/10 to-[#2C4358]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      <div className="relative bg-white/90 backdrop-blur-sm border-2 border-[#22B6B2]/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 flex flex-col gap-2 sm:gap-3 lg:gap-4 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
        {/* Barra de estado superior */}
        <div 
          className="h-1 w-full rounded-full"
          style={{ backgroundColor: statusColors[responsible.status] || '#9CA3AF' }}
        />
        
        <div className="flex items-start justify-between gap-2 sm:gap-3 lg:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-[#22B6B2]/20 to-[#2C4358]/20 rounded-full flex items-center justify-center border-2 border-[#22B6B2]/30 flex-shrink-0">
                <span className="text-sm sm:text-lg lg:text-xl">
                  {responsible.roleName === 'kitchen' ? '👨‍🍳' : '👔'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs sm:text-sm lg:text-base font-black text-[#2C4358] mb-1 truncate">{responsible.email}</div>
                <div className="text-xs sm:text-sm text-[#85929e] font-bold truncate">
                  Agregado: {formatDate(responsible.createdAt)}
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1 sm:gap-2 lg:gap-3">
              <span 
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-black rounded-full text-white shadow-lg flex-shrink-0"
                style={{ backgroundColor: statusColors[responsible.status] }}
              >
                {statusLabels[responsible.status]}
              </span>
              <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-black rounded-full bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white shadow-lg flex-shrink-0">
                {roleLabels[responsible.roleName]}
              </span>
              {responsible.registrationStatus && (
                <span 
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-black rounded-full text-white shadow-lg flex-shrink-0"
                  style={{ backgroundColor: registrationColors[responsible.registrationStatus] }}
                >
                  {registrationLabels[responsible.registrationStatus]}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex gap-1 sm:gap-2 lg:gap-3 ml-2 sm:ml-4 flex-shrink-0">
            <button
              onClick={() => onEdit(responsible)}
              className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] rounded-lg sm:rounded-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 flex items-center justify-center"
              title="Editar"
            >
              <span className="text-xs sm:text-sm lg:text-base">✏️</span>
            </button>
            {responsible.status !== 'pending' && (
              <button
                onClick={() => onToggleStatus(responsible)}
                className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] rounded-lg sm:rounded-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 flex items-center justify-center"
                title={responsible.status === 'active' ? 'Desactivar' : 'Activar'}
              >
                <span className="text-xs sm:text-sm lg:text-base">{responsible.status === 'active' ? '⏸️' : '▶️'}</span>
              </button>
            )}
            <button
              onClick={() => onDelete(responsible)}
              className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-red-500/10 to-red-600/10 hover:from-red-500/20 hover:to-red-600/20 text-red-600 hover:text-red-700 rounded-lg sm:rounded-xl transition-all duration-300 border-2 border-red-500/30 hover:border-red-500/50 hover:scale-110 flex items-center justify-center"
              title="Eliminar"
            >
              <span className="text-xs sm:text-sm lg:text-base">🗑️</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ResponsiblesPage() {
  const { user, userRole } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  
  // Determinar roles y permisos
  const isUserOwner = userRole?.id === 'owner';
  const isUserManager = userRole?.id === 'manager';
  const canManageResponsibles = isUserOwner || isUserManager;
  
  const [responsibles, setResponsibles] = useState<Responsible[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResponsible, setEditingResponsible] = useState<Responsible | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newResponsibleData, setNewResponsibleData] = useState<CreateResponsibleData>({
    email: '',
    role_name: isUserManager ? 'kitchen' : 'manager',
  });
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  // Cargar responsables cuando cambie el restaurante seleccionado
  useEffect(() => {
    const currentRestaurantId = selectedRestaurantId;
    const currentUserId = user?.uid;
    
    // Si es owner/manager y no ha seleccionado restaurante, limpiar responsables
    if (canManageResponsibles && !selectedRestaurantId) {
      console.log('🧹 Usuario no ha seleccionado restaurante, limpiando responsables');
      setResponsibles([]);
      setLoading(false);
      return;
    }
    
    // Solo cargar responsables si hay un restaurante seleccionado y usuario
    if (currentRestaurantId && currentUserId) {
      console.log('🔄 Restaurante seleccionado, cargando responsables:', currentRestaurantId);
      
      // Limpiar responsables inmediatamente
      setResponsibles([]);
      setLoading(true);
      
      const fetchResponsibles = async () => {
        try {
          console.log('🔄 Loading responsibles for restaurant:', currentRestaurantId);
          
          const response = await responsiblesService.getResponsiblesByRestaurant(currentUserId, currentRestaurantId);
          
          // Verificar si hay duplicados y loggear para debug
          const responsibleIds = response.data.map(r => r.id);
          const uniqueIds = [...new Set(responsibleIds)];
          
          if (responsibleIds.length !== uniqueIds.length) {
            console.log('⚠️ Se encontraron responsables duplicados:', {
              total: response.data.length,
              unique: uniqueIds.length,
              duplicateIds: responsibleIds.filter((id, index) => responsibleIds.indexOf(id) !== index)
            });
            
            // Si hay IDs duplicados, usar email como identificador único
            const uniqueResponsibles = response.data.filter((responsible, index, self) => 
              index === self.findIndex(r => r.email === responsible.email)
            );
            
            console.log('📊 Responsables cargados (usando email como ID único):', {
              original: response.data.length,
              filtered: uniqueResponsibles.length,
              responsibles: uniqueResponsibles.map(r => ({ id: r.id, email: r.email, status: r.status }))
            });
            
            setResponsibles(uniqueResponsibles);
          } else {
            // Si no hay duplicados, usar la lógica normal
            console.log('📊 Responsables cargados:', {
              total: response.data.length,
              responsibles: response.data.map(r => ({ id: r.id, email: r.email, status: r.status }))
            });
            
            setResponsibles(response.data);
          }
          
          console.log('✅ Responsibles loaded:', response.data.length);
        } catch (error) {
          console.error('❌ Error loading responsibles:', error);
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            setErrorModal({ open: true, title: 'No se pudieron cargar los responsables', message: error.message });
          } else {
            setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudieron cargar los responsables' });
          }
        } finally {
          setLoading(false);
        }
      };
      
      fetchResponsibles();
    }
  }, [selectedRestaurantId, user?.uid, canManageResponsibles]);

  // Actualizar rol por defecto según el usuario
  useEffect(() => {
    setNewResponsibleData(prev => ({
      ...prev,
      role_name: isUserManager ? 'kitchen' : 'manager'
    }));
  }, [isUserManager]);

  const onRefresh = async () => {
    const currentRestaurantId = selectedRestaurantId;
    const currentUserId = user?.uid;
    
    // Si es owner/manager y no ha seleccionado restaurante, no hacer refresh
    if (canManageResponsibles && !selectedRestaurantId) {
      console.log('Usuario no ha seleccionado restaurante, saltando refresh');
      setRefreshing(false);
      return;
    }
    
    if (!currentRestaurantId || !currentUserId) {
      setRefreshing(false);
      return;
    }
    
    setRefreshing(true);
    try {
      const response = await responsiblesService.getResponsiblesByRestaurant(currentUserId, currentRestaurantId);
      
      // Verificar si hay duplicados y aplicar la misma lógica
      const responsibleIds = response.data.map(r => r.id);
      const uniqueIds = [...new Set(responsibleIds)];
      
      if (responsibleIds.length !== uniqueIds.length) {
        // Si hay IDs duplicados, usar email como identificador único
        const uniqueResponsibles = response.data.filter((responsible, index, self) => 
          index === self.findIndex(r => r.email === responsible.email)
        );
        setResponsibles(uniqueResponsibles);
      } else {
        // Si no hay duplicados, usar la lógica normal
        setResponsibles(response.data);
      }
    } catch (error) {
      console.error('❌ Error refreshing responsibles:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddResponsible = () => {
    setNewResponsibleData({ 
      email: '', 
      role_name: isUserManager ? 'kitchen' : 'manager' 
    });
    setEditingResponsible(null);
    setShowAddModal(true);
  };

  const handleEditResponsible = (responsible: Responsible) => {
    setEditingResponsible(responsible);
    setNewResponsibleData({ 
      email: responsible.email, 
      role_name: responsible.roleName 
    });
    setShowAddModal(true);
  };

  const handleSaveResponsible = async () => {
    const currentRestaurantId = selectedRestaurantId;
    
    if (!user?.uid || !currentRestaurantId) {
      setErrorModal({ open: true, title: 'Datos faltantes', message: 'Usuario no autenticado o restaurante no seleccionado' });
      return;
    }

    if (!newResponsibleData.email.trim()) {
      setErrorModal({ open: true, title: 'Email inválido', message: 'Por favor ingresa un email válido' });
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (editingResponsible) {
        await responsiblesService.updateResponsible(
          user.uid,
          currentRestaurantId,
          editingResponsible.id,
          { email: newResponsibleData.email, role_name: newResponsibleData.role_name }
        );
      } else {
        await responsiblesService.createResponsible(
          user.uid,
          currentRestaurantId,
          newResponsibleData
        );
      }
      
      // Limpiar formulario
      setNewResponsibleData({ 
        email: '', 
        role_name: isUserManager ? 'kitchen' : 'manager' 
      });
      setShowAddModal(false);
      
      // Recargar responsables
      const response = await responsiblesService.getResponsiblesByRestaurant(user.uid, currentRestaurantId);
      
      // Verificar si hay duplicados y aplicar la misma lógica
      const responsibleIds = response.data.map(r => r.id);
      const uniqueIds = [...new Set(responsibleIds)];
      
      if (responsibleIds.length !== uniqueIds.length) {
        // Si hay IDs duplicados, usar email como identificador único
        const uniqueResponsibles = response.data.filter((responsible, index, self) => 
          index === self.findIndex(r => r.email === responsible.email)
        );
        setResponsibles(uniqueResponsibles);
      } else {
        // Si no hay duplicados, usar la lógica normal
        setResponsibles(response.data);
      }
    } catch (error) {
      console.error('❌ Error saving responsible:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: editingResponsible ? 'No se pudo actualizar' : 'No se pudo agregar', message: (error as ApiError).message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: editingResponsible ? 'No se pudo actualizar el responsable' : 'No se pudo agregar el responsable' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (responsible: Responsible) => {
    const currentRestaurantId = selectedRestaurantId;
    
    if (!user?.uid || !currentRestaurantId) return;
    
    try {
      setLoading(true);
      const newStatus = responsible.status === 'active' ? 'inactive' : 'active';
      await responsiblesService.updateResponsible(
        user.uid,
        currentRestaurantId,
        responsible.id,
        { status: newStatus }
      );
      
      // Recargar responsables
      const response = await responsiblesService.getResponsiblesByRestaurant(user.uid, currentRestaurantId);
      
      // Verificar si hay duplicados y aplicar la misma lógica
      const responsibleIds = response.data.map(r => r.id);
      const uniqueIds = [...new Set(responsibleIds)];
      
      if (responsibleIds.length !== uniqueIds.length) {
        // Si hay IDs duplicados, usar email como identificador único
        const uniqueResponsibles = response.data.filter((responsible, index, self) => 
          index === self.findIndex(r => r.email === responsible.email)
        );
        setResponsibles(uniqueResponsibles);
      } else {
        // Si no hay duplicados, usar la lógica normal
        setResponsibles(response.data);
      }
      
      // Éxito silencioso; el estado visual se actualiza
    } catch (error) {
      console.error('❌ Error toggling status:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo cambiar estado', message: (error as ApiError).message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo cambiar el estado del responsable' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResponsible = async (responsible: Responsible) => {
    const currentRestaurantId = selectedRestaurantId;
    
    if (!user?.uid || !currentRestaurantId) return;
    
    if (confirm(`¿Estás seguro de eliminar a ${responsible.email}?`)) {
      try {
        setLoading(true);
        await responsiblesService.deleteResponsible(
          user.uid,
          currentRestaurantId,
          responsible.id
        );
        
        // Recargar responsables
        const response = await responsiblesService.getResponsiblesByRestaurant(user.uid, currentRestaurantId);
        
        // Verificar si hay duplicados y aplicar la misma lógica
        const responsibleIds = response.data.map(r => r.id);
        const uniqueIds = [...new Set(responsibleIds)];
        
        if (responsibleIds.length !== uniqueIds.length) {
          // Si hay IDs duplicados, usar email como identificador único
          const uniqueResponsibles = response.data.filter((responsible, index, self) => 
            index === self.findIndex(r => r.email === responsible.email)
          );
          setResponsibles(uniqueResponsibles);
        } else {
          // Si no hay duplicados, usar la lógica normal
          setResponsibles(response.data);
        }
        
        // Opcional: podríamos mostrar un InlineAlert de éxito si se requiere feedback
      } catch (error) {
        console.error('❌ Error deleting responsible:', error);
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          setErrorModal({ open: true, title: 'No se pudo eliminar', message: (error as ApiError).message });
        } else {
          setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo eliminar el responsable' });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredResponsibles = responsibles.filter(responsible =>
    responsible.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mostrar loading mientras se valida
  if (loading && responsibles.length === 0) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin" style={{animationDelay: '0.5s'}}></div>
          </div>
          <p className="text-[#2C4358] font-bold">Cargando responsables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header futurista */}
      <div className="bg-white/90 backdrop-blur-sm border-b-2 border-[#22B6B2]/20 px-5 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="w-10 h-10 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] rounded-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 flex items-center justify-center disabled:opacity-50"
              title="Actualizar"
            >
              {refreshing ? (
                <div className="w-5 h-5 border-2 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin"></div>
              ) : (
                <span className="text-lg">🔄</span>
              )}
            </button>
            <button
              onClick={handleAddResponsible}
              disabled={!selectedRestaurantId}
              className="bg-[#f7ebd6] text-[#2C4358] px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-black disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:bg-[#f0e0c0]"
            >
              {/* Efecto de brillo */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <span className="relative z-10">➕ Agregar Responsable</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar futurista */}
      <div className="bg-white/90 backdrop-blur-sm border-b-2 border-[#22B6B2]/20 px-5 py-6">
        <div className="relative">
          <input
            type="text"
            placeholder="🔍 Buscar por email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:ring-4 focus:ring-[#22B6B2]/30 focus:border-[#22B6B2] bg-white/80 backdrop-blur-sm font-bold text-[#2C4358] transition-all duration-300"
          />
          <span className="absolute left-4 top-3 text-[#85929e] text-lg">🔍</span>
        </div>
      </div>

      {/* Content futurista */}
      <div className="p-5 sm:p-6">
        {!selectedRestaurantId && canManageResponsibles ? (
          <div className="text-center py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/20 to-[#2C4358]/20 rounded-full blur-2xl"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-[#22B6B2]/30 to-[#2C4358]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🏪</span>
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-[#2C4358] mb-3">
              Selecciona un restaurante
            </h3>
            <p className="text-[#85929e] font-bold text-sm sm:text-base max-w-md mx-auto">
              Para ver y gestionar los responsables, primero selecciona un restaurante
            </p>
          </div>
        ) : filteredResponsibles.length === 0 ? (
          <div className="text-center py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/20 to-[#2C4358]/20 rounded-full blur-2xl"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-[#22B6B2]/30 to-[#2C4358]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">👥</span>
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-[#2C4358] mb-3">
              {searchTerm ? 'No se encontraron responsables' : 'No hay responsables'}
            </h3>
            <p className="text-[#85929e] font-bold text-sm sm:text-base max-w-md mx-auto mb-6">
              {searchTerm 
                ? 'Intenta con otro término de búsqueda'
                : 'Agrega el primer responsable para comenzar'
              }
            </p>
            {!searchTerm && selectedRestaurantId && (
              <button
                onClick={handleAddResponsible}
                className="bg-[#f7ebd6] text-[#2C4358] px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-black relative overflow-hidden group border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:bg-[#f0e0c0]"
              >
                {/* Efecto de brillo */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative z-10">➕ Agregar Responsable</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {filteredResponsibles.map((responsible, index) => (
              <ResponsibleCard
                key={`${responsible.id || responsible.email}-${responsible.status}-${index}`}
                responsible={responsible}
                onEdit={handleEditResponsible}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteResponsible}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal futurista para agregar/editar responsable */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white/90 backdrop-blur-md border-2 border-[#22B6B2]/20 rounded-3xl shadow-2xl w-full max-w-md mx-2 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Fondo animado sutil */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
            {/* Header fijo */}
            <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-6 py-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">👨‍💼</span>
                  <h2 className="text-lg sm:text-xl font-black text-[#2C4358]">
                    {editingResponsible ? 'Editar Responsable' : 'Agregar Responsable'}
                  </h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] text-lg sm:text-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 group"
                >
                  <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
                </button>
              </div>
            </div>
            {/* Body scrolleable con borde redondeado */}
            <div className="overflow-hidden rounded-b-3xl flex-1 flex flex-col">
              <div className="relative z-10 p-6 overflow-y-auto h-full">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-black text-[#2C4358] mb-3">
                      📧 Email
                    </label>
                    <input
                      type="email"
                      value={newResponsibleData.email}
                      onChange={(e) => setNewResponsibleData({...newResponsibleData, email: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30 focus:border-[#22B6B2] text-sm font-bold bg-white/80 backdrop-blur-sm transition-all duration-300"
                      placeholder="email@ejemplo.com"
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-black text-[#2C4358] mb-3">
                      🎭 Rol
                    </label>
                    <select
                      value={newResponsibleData.role_name}
                      onChange={(e) => setNewResponsibleData({...newResponsibleData, role_name: e.target.value as 'manager' | 'kitchen'})}
                      className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30 focus:border-[#22B6B2] text-sm font-bold bg-white/80 backdrop-blur-sm transition-all duration-300"
                      disabled={isSubmitting}
                    >
                      <option value="manager">👔 Gerente</option>
                      <option value="kitchen">👨‍🍳 Cocina</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-8">
                  <button
                    onClick={() => setShowAddModal(false)}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 text-[#85929e] hover:text-[#2C4358] transition-all duration-300 text-sm font-black rounded-xl hover:bg-[#22B6B2]/10 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 disabled:opacity-50"
                  >
                    ❌ Cancelar
                  </button>
                  <button
                    onClick={handleSaveResponsible}
                    disabled={!newResponsibleData.email || isSubmitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] hover:from-[#1a8f8c] hover:to-[#1e2f3f] disabled:from-gray-400 disabled:to-gray-600 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-black relative overflow-hidden group"
                  >
                    {/* Efecto de brillo */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative z-10">
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
                          {editingResponsible ? '⏳ Actualizando...' : '⏳ Agregando...'}
                        </div>
                      ) : (
                        editingResponsible ? '💾 Actualizar' : '➕ Agregar'
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
}
